import { UseMutationOptions } from "@tanstack/react-query";
import { type AxiosRequestConfig } from "axios";
import {
  BankConnectionsApiGetBankConnectionV2Request,
  AccountsApiListAndSearchAllRemoteAccountsRequest,
  AccountsApiListAndSearchAllAccountsRequest,
  BankConnectionsApiDeleteStoredSecretsV2Request,
  BankConnectionsApiListAndSearchAllBankConnectionsV2Request,
  UserProfilesApiProfileImageRequest,
  UserProfilesApiSetUserProfileImageRequest,
  buildUserProfilesApiWithToken,
  buildBankConnectionsApiWithToken as buildFinApiBankConnectionsApiWithToken,
  buildAccountsApiWithToken,
} from "@rentablo/api/apiv1";
import {
  BankConnectionData,
  BankConnectionsApiGetBankConnectionsInBatchRequest,
  buildBankConnectionsApiWithToken as buildWealthApiBankConnectionsApiWithToken,
} from "@rentablo/api/apiv3";
import {
  axiosInstanceV1,
  useAuthMutation,
  useAuthQuery,
  UseQueryExtendedOptions,
} from "@rentablo/api";

import {
  GET_BANK_CONNECTION,
  GET_BANK_CONNECTIONS,
  GET_REMOTE_ACCOUNTS,
  GET_USER_PROFILE_IMAGE,
} from "@api/cacheKeys";
import {
  transformBankConnection,
  transformBankConnectionAccount,
} from "@features/settings/SettingsBankConnections/helpers";
import {
  SettingsBankConnection,
  SettingsBankConnectionAccount,
} from "@features/settings/SettingsBankConnections/types";

export const getBankConnection = async (
  provider: "FIN_API" | "WEALTH_API",
  params: BankConnectionsApiGetBankConnectionV2Request
) => {
  if (provider === "FIN_API") {
    const bankConnectionsApi = await buildFinApiBankConnectionsApiWithToken();
    const { data } = await bankConnectionsApi.getBankConnectionV2(params);
    return transformBankConnection(data);
  }
  const bankConnectionsApi = await buildWealthApiBankConnectionsApiWithToken();
  const { data } = await bankConnectionsApi.getBankConnection(params);
  return transformBankConnection(data?.data as BankConnectionData);
};

export const getAccountsByProvider = async (
  provider: "FIN_API" | "WEALTH_API",
  params:
    | AccountsApiListAndSearchAllRemoteAccountsRequest
    | AccountsApiListAndSearchAllAccountsRequest
) => {
  const accountsApi = await buildAccountsApiWithToken();
  const { data } = await (provider === "FIN_API"
    ? accountsApi.listAndSearchAllRemoteAccounts(params)
    : accountsApi.listAndSearchAllAccounts(params));
  return data?.accounts?.map(transformBankConnectionAccount);
};

export const getWealthApiBankConnections = async (
  params: BankConnectionsApiGetBankConnectionsInBatchRequest
) => {
  const bankConnectionsApi = await buildWealthApiBankConnectionsApiWithToken();
  const { data } = await bankConnectionsApi.getBankConnectionsInBatch(params);
  return data?.data.map(transformBankConnection);
};

export const getFinApiBankConnections = async (
  params: BankConnectionsApiListAndSearchAllBankConnectionsV2Request
) => {
  const bankConnectionsApi = await buildFinApiBankConnectionsApiWithToken();
  const { data } =
    await bankConnectionsApi.listAndSearchAllBankConnectionsV2(params);
  return data?.bankConnections?.map(transformBankConnection);
};

export const deleteStoredSecrets =
  (provider: "FIN_API" | "WEALTH_API") =>
  async (
    token: string | undefined,
    params: BankConnectionsApiDeleteStoredSecretsV2Request
  ) => {
    if (provider === "FIN_API") {
      const bankConnectionsApi = await buildFinApiBankConnectionsApiWithToken();
      return bankConnectionsApi.deleteStoredSecretsV2(params);
    } else {
      const bankConnectionsApi =
        await buildWealthApiBankConnectionsApiWithToken();
      return bankConnectionsApi.clearCredentials(params);
    }
  };

export const useGetBankConnectionByProvider = (
  provider: "FIN_API" | "WEALTH_API",
  params: { id: number },
  options?: UseQueryExtendedOptions<
    unknown,
    AxiosApiError,
    SettingsBankConnection
  >
) => {
  return useAuthQuery(
    [GET_BANK_CONNECTION, provider, params],
    () => getBankConnection(provider, params),
    options
  );
};

export const useGetAccountsByProvider = (
  provider: "FIN_API" | "WEALTH_API",
  params:
    | AccountsApiListAndSearchAllRemoteAccountsRequest
    | AccountsApiListAndSearchAllAccountsRequest,
  options?: UseQueryExtendedOptions<
    unknown,
    AxiosApiError,
    SettingsBankConnectionAccount[]
  >
) => {
  return useAuthQuery(
    [GET_REMOTE_ACCOUNTS, provider, params],
    () => getAccountsByProvider(provider, params),
    options
  );
};

export const useDeleteStoredSecrets = (
  provider: "FIN_API" | "WEALTH_API",
  options?: UseMutationOptions<
    unknown,
    AxiosApiError,
    BankConnectionsApiDeleteStoredSecretsV2Request
  >
) => {
  return useAuthMutation(deleteStoredSecrets(provider), options);
};

export const useGetWealthApiBankConnections = (
  params: BankConnectionsApiGetBankConnectionsInBatchRequest,
  options?: UseQueryExtendedOptions<
    unknown,
    AxiosApiError,
    SettingsBankConnection[]
  >
) => {
  return useAuthQuery(
    [GET_BANK_CONNECTIONS, "WEALTH_API"],
    () => getWealthApiBankConnections(params),
    options
  );
};

const options: AxiosRequestConfig = {
  responseType: "blob",
  headers: {
    Accept: "image/png",
    "content-type": "image/png",
  },
};
export const updateUserProfileImage = async (
  token: string | undefined,
  params: UserProfilesApiSetUserProfileImageRequest
) => {
  const userProfilesApi = await buildUserProfilesApiWithToken();
  return userProfilesApi.setUserProfileImage(params, options);
};

export const getUserProfileImage = async (
  params: UserProfilesApiProfileImageRequest
) => {
  const userProfilesApi = await buildUserProfilesApiWithToken();
  const { data } = await userProfilesApi.profileImage(params, options);
  return data;
};

export const useGetUserProfileImage = (
  params: UserProfilesApiProfileImageRequest,
  options?: UseQueryExtendedOptions<unknown, AxiosApiError, Blob>
) => {
  return useAuthQuery(
    [GET_USER_PROFILE_IMAGE, params],
    () => getUserProfileImage(params),
    options
  );
};

export const useUserProfileImageUpdate = (
  options?: UseMutationOptions<
    unknown,
    AxiosApiError,
    UserProfilesApiProfileImageRequest
  >
) => {
  return useAuthMutation(updateUserProfileImage, options);
};

const sendKpmgReport = async (
  token: string | undefined,
  params: { userId: number }
) => {
  const { data } = await axiosInstanceV1.post(
    `/qualityAssurance/sendKpmgReport/${params.userId}`,
    undefined,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useSendKpmgReport = (
  options?: UseMutationOptions<unknown, AxiosApiError, { userId: number }>
) => {
  return useAuthMutation(sendKpmgReport, options);
};
